import React, { useState, useRef, useCallback } from 'react';
import CameraCapture from '../CameraCapture/CameraCapture';
import { uploadPhotos } from '../../utils/photoUpload';

const PhotoUpload = ({ projectId, onUploadComplete }) => {
    const [uploading, setUploading] = useState(false);
    const [completed, setCompleted] = useState(0);
    const [total, setTotal] = useState(0);
    const [error, setError] = useState('');
    const [dragOver, setDragOver] = useState(false);
    const [showCamera, setShowCamera] = useState(false);
    const fileInputRef = useRef(null);
    const dropRef = useRef(null);

    const processFiles = useCallback(async (files) => {
        if (files.length === 0 || uploading) return;

        setError('');
        setUploading(true);
        setTotal(0);
        setCompleted(0);

        // Validation, compression, and the worker pool all live in the helper now.
        // So this component is only responsible for showing what happened

        const result = await uploadPhotos(projectId, files, (done, batchTotal) => {
            setCompleted(done);
            setTotal(batchTotal);
        });

        // Worded here rather than in the helper: the job form says something different
        // about the same result, because there the photos are not lost if they fail.
        const notImages = result.skipped.filter(s => s.reason === 'not an image').length;
        const tooBig = result.skipped.filter(s => s.reason === 'over 50MB').length;

        const problems = [];
        if (notImages > 0) problems.push(`${notImages} non-image file(s) were skipped`);
        if (tooBig > 0) problems.push(`${tooBig} file(s) exceed 50MB and were skipped`);
        if (result.failed.length > 0) {
            problems.push(`${result.failed.length} of ${result.total} uploads failed`);
        }
        setError(problems.join('. '));


        setUploading(false);
        setCompleted(0);
        setTotal(0);
        if (fileInputRef.current) fileInputRef.current.value = '';
        if (onUploadComplete) onUploadComplete();
    }, [uploading, projectId, onUploadComplete]);

    const handleFileChange = (e) => {
        processFiles(Array.from(e.target.files));
    };

    const handleCameraCapture = (file) => {
        setShowCamera(false);
        processFiles([file]);
    };

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!uploading) setDragOver(true);
    }, [uploading]);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(false);
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(false);
        if (uploading) return;

        const files = Array.from(e.dataTransfer.files);
        processFiles(files);
    }, [uploading, processFiles]);

    return (
        <div className="photo-upload-wrapper">
            {/* Drop Zone */}
            <div
                ref={dropRef}
                className={`photo-drop-zone${dragOver ? ' drag-over' : ''}${uploading ? ' uploading' : ''}`}
                onDragOver={handleDragOver}
                onDragEnter={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => !uploading && fileInputRef.current?.click()}
            >
                <input
                    type="file"
                    ref={fileInputRef}
                    accept="image/*"
                    multiple
                    onChange={handleFileChange}
                    disabled={uploading}
                    style={{ display: 'none' }}
                />

                {uploading ? (
                    <div className="drop-zone-uploading">
                        <div className="upload-progress-bar">
                            <div
                                className="upload-progress-fill"
                                style={{ width: `${total > 0 ? (completed / total) * 100 : 0}%` }}
                            />
                        </div>
                        <span className="upload-progress-text">
                            Uploading {completed}/{total}...
                        </span>
                    </div>
                ) : (
                    <div className="drop-zone-content">
                        <i className="fas fa-cloud-upload-alt drop-zone-icon"></i>
                        <span className="drop-zone-text">
                            Drag & drop photos here or <span className="drop-zone-browse">browse</span>
                        </span>
                        <span className="drop-zone-hint">Supports multiple images up to 50MB each</span>
                    </div>
                )}
            </div>

            <button
                type="button"
                className="photo-camera-btn"
                onClick={() => setShowCamera(true)}
                disabled={uploading}
                style={{
                    marginTop: '10px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '9px 16px',
                    border: '1px solid var(--border-color, #d1d5db)',
                    borderRadius: '8px',
                    background: 'var(--card-bg, #fff)',
                    color: 'var(--text-color, #374151)',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: uploading ? 'default' : 'pointer',
                    opacity: uploading ? 0.5 : 1,
                }}
            >
                <i className="fas fa-camera"></i> Take Photo
            </button>

            {error && <div className="photo-upload-error">{error}</div>}

            {showCamera && (
                <CameraCapture onCapture={handleCameraCapture} onClose={() => setShowCamera(false)} />
            )}
        </div>
    );
};

export default PhotoUpload;
